import { Vec3 } from '@/core/math/Vec3';
import { CelestialBody } from '@/universe/CelestialBody';
import { SolarSystem } from '@/universe/SolarSystem';

export interface GravityContribution {
  body: CelestialBody;
  /** Acceleration magnitude, m/s^2. */
  magnitude: number;
  /** Distance from the body's centre, metres. */
  distance: number;
}

export interface GravitySample {
  /** Total acceleration in the root frame, m/s^2. */
  acceleration: Vec3;
  /** The single largest contributor, for UI and orbit-frame selection. */
  dominant: CelestialBody;
  dominantMagnitude: number;
  /** Every contributor above the reporting threshold, strongest first. */
  contributions: GravityContribution[];
}

/**
 * Gravity for all dynamic bodies.
 *
 * Design decision: this sums *every* celestial body every step rather than
 * using patched conics or a sphere-of-influence switch.
 *
 * With roughly twenty bodies that is about twenty square roots per dynamic
 * object per step, which is nothing, and it buys a property that matters a
 * great deal: the acceleration field is continuous everywhere. There is no
 * invisible boundary where a trajectory kinks, no discontinuity for the time
 * warp safety checks to trip over, and no special case when a vessel is
 * genuinely influenced by two bodies at once (say, at a Lagrange region).
 *
 * Gravity is strictly one-way. Planets are propagated analytically and are
 * never displaced by a vessel, cargo, asteroid or projectile.
 */
export class GravitySystem {
  /** Bodies whose acceleration is below this are omitted from diagnostics. */
  reportingThreshold = 1e-7;

  constructor(private readonly system: SolarSystem) {}

  /**
   * Acceleration at a root-frame position, in m/s^2.
   * Allocation-free apart from the output vector.
   */
  accelerationAt(positionInRoot: Vec3, t: number, out = new Vec3()): Vec3 {
    out.set(0, 0, 0);
    const bodies = this.system.bodies;
    for (let i = 0; i < bodies.length; i++) {
      const body = bodies[i];
      const bp = this.system.frames.stateInRoot(body.inertialFrameId, t).position;
      const dx = bp.x - positionInRoot.x;
      const dy = bp.y - positionInRoot.y;
      const dz = bp.z - positionInRoot.z;
      const r2 = dx * dx + dy * dy + dz * dz;
      if (r2 <= 0) continue;
      const r = Math.sqrt(r2);

      // Inside the body, taper linearly to zero at the centre. This is the
      // correct behaviour for a uniform sphere and, more practically, stops the
      // integrator from producing infinities if something clips through terrain.
      let a: number;
      if (r < body.radius) {
        a = (body.mu / (body.radius * body.radius)) * (r / body.radius);
      } else {
        a = body.mu / r2;
      }
      const inv = a / r;
      out.x += dx * inv;
      out.y += dy * inv;
      out.z += dz * inv;
    }
    return out;
  }

  /** Full sample including the contributor breakdown, for diagnostics. */
  sample(positionInRoot: Vec3, t: number): GravitySample {
    const acceleration = new Vec3();
    const contributions: GravityContribution[] = [];
    let dominant = this.system.star;
    let dominantMagnitude = -Infinity;

    for (const body of this.system.bodies) {
      const bp = this.system.frames.stateInRoot(body.inertialFrameId, t).position;
      const delta = Vec3.subVectors(bp, positionInRoot);
      const r = delta.length();
      if (r <= 0) continue;
      const magnitude = body.gravityAt(r);
      acceleration.addScaled(delta.mulScalar(1 / r), magnitude);
      if (magnitude > dominantMagnitude) {
        dominantMagnitude = magnitude;
        dominant = body;
      }
      if (magnitude >= this.reportingThreshold) {
        contributions.push({ body, magnitude, distance: r });
      }
    }

    contributions.sort((a, b) => b.magnitude - a.magnitude);
    return { acceleration, dominant, dominantMagnitude, contributions };
  }

  /**
   * Acceleration expressed in a body's inertial frame rather than the root
   * frame. Because that frame is itself accelerating toward the star, the
   * parent's pull on the frame origin is subtracted; what remains is the
   * "tidal" residual plus the body's own gravity. This is the correct
   * acceleration to integrate when a vessel is stored in planet-relative
   * coordinates.
   */
  accelerationInBodyFrame(body: CelestialBody, positionInBodyFrame: Vec3, t: number, out = new Vec3()): Vec3 {
    const bodyState = this.system.frames.stateInRoot(body.inertialFrameId, t);
    const rootPos = Vec3.addVectors(bodyState.position, positionInBodyFrame);
    this.accelerationAt(rootPos, t, out);
    // Subtract the acceleration of the frame origin itself.
    const originAccel = this.accelerationAtExcluding(bodyState.position, t, body);
    return out.sub(originAccel);
  }

  /** Acceleration at a point, ignoring one body (used for frame corrections). */
  accelerationAtExcluding(positionInRoot: Vec3, t: number, exclude: CelestialBody, out = new Vec3()): Vec3 {
    out.set(0, 0, 0);
    for (const body of this.system.bodies) {
      if (body === exclude) continue;
      const bp = this.system.frames.stateInRoot(body.inertialFrameId, t).position;
      const delta = Vec3.subVectors(bp, positionInRoot);
      const r = delta.length();
      if (r <= 0) continue;
      out.addScaled(delta.mulScalar(1 / r), body.gravityAt(r));
    }
    return out;
  }
}
